import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Skeleton } from '@/components/ui/Feedback';
import { SopCard, useSops } from '@/features/company/SopsPage';
import { findNavMatch, sidebarItemFor } from '@/nav/navigation';

/**
 * The "?" at the top of every page (client round 6).
 *
 * It opens the procedure for the module you are standing in — the Tendering SOP
 * on any Pre-Construction screen, Project Delivery on a project — with a link
 * to the live document where the client keeps it. The client's experience:
 * a document you have to click away to find is a document nobody reads.
 */
export function HelpButton() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const sops = useSops();

  const module = sidebarItemFor(location.pathname);
  const here = findNavMatch(location.pathname).item;
  const moduleIds = new Set([module?.id, here?.id, here?.module].filter(Boolean));
  const matching = (sops.data?.sops ?? []).filter((sop) => sop.module && moduleIds.has(sop.module));

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Help and SOPs for this page"
        title="Help & SOPs"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cloud text-body transition-colors hover:bg-digital-50 hover:text-digital"
      >
        <HelpCircle aria-hidden className="h-[18px] w-[18px]" />
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        size="lg"
        title={module ? `${module.label} — how we do it` : 'Help & SOPs'}
        description="The procedure for this part of the business, and the live document it comes from."
      >
        {sops.isPending ? (
          <Skeleton className="h-40 rounded-lg" />
        ) : matching.length === 0 ? (
          <p className="text-sm text-muted">
            No SOP is linked to {module?.label ?? 'this page'} yet.{' '}
            <Link to="/company/sops" onClick={() => setOpen(false)} className="font-semibold text-digital hover:underline">
              Open the SOP register
            </Link>{' '}
            to see every procedure.
          </p>
        ) : (
          <div className="space-y-3">
            {matching.map((sop) => (
              <SopCard
                key={sop.id}
                sop={sop}
                open={expanded === sop.id || matching.length === 1}
                onToggle={() => setExpanded(expanded === sop.id ? null : sop.id)}
                canEdit={sops.data?.can_edit}
              />
            ))}
          </div>
        )}
      </Modal>
    </>
  );
}
